/**
 * The pieces the Well Testing AI pages draw (WT-6, fieldiq-api#1261): the
 * deterministic figures of each run as rows, and the AI draft with its review.
 * PF-6's PfAiBits, for this vertical — the same layout and the same review
 * rules, over WT's figures (a shift's window, pressures, rates, cumulatives,
 * flags; a preflight's packages; PSV recertifications and calibrations).
 *
 * THE FIGURES ARE THE API'S DETERMINISTIC LAYER, shown first and in full. The
 * AI wrote only the draft text below them. A pending draft reads "awaiting
 * your approval" and says nothing is published; it is approved (optionally
 * edited, on the page) or dismissed through the WT doors. A run whose draft
 * failed still has correct figures, and says so with the API's reason.
 *
 * Every value that did not arrive renders `—`; a real 0 renders `0`.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ListChecks, Loader2, Pencil, Sparkles, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useReviewWtAi, useRunWtAi, useWtAiDigest } from '../../../hooks/useWtAi';
import { useWtJobs } from '../../../hooks/useWellTesting';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { formatInstant } from '../../../lib/calendar-date';
import { withUnit, wtDay } from '../../../lib/well-testing';
import { WT_AI_MAX_NARRATIVE, WT_AI_MAX_OBSERVATION, wtAiRefusal, wtAiStateSkin, wtShiftLabel } from '../../../lib/wt-ai';
import type {
  WtAiDraft,
  WtAiRunResult,
  WtComplianceDigestData,
  WtPreflightData,
  WtPreflightRateGap,
  WtReportDraftData,
  WtShiftCumulative,
  WtShiftFigure,
  WtShiftSummaryData,
} from '../../../types/wt-ai';
import { WT_DATE_INPUT, WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtBadge, WtEmpty, WtField, WtFormError, WtPanel, WtSpec } from '../WtBits';

const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const text = (v: unknown): string => (typeof v === 'string' && v.trim() ? v : '—');
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

// ─── A small read-only list of rows ──────────────────────────────────────────

interface Col<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  align?: 'right';
}

function AiTable<T>({ rows, cols, rowKey, rail, empty, testId, minWidth = 'min-w-[640px]' }: {
  rows: T[];
  cols: Col<T>[];
  rowKey: (row: T, i: number) => string;
  rail?: (row: T) => string;
  empty: string;
  testId: string;
  minWidth?: string;
}) {
  if (rows.length === 0) return <WtEmpty>{empty}</WtEmpty>;
  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${minWidth} text-left`} data-testid={testId}>
        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
          <tr className="border-l-4 border-l-transparent">
            {cols.map((c) => (
              <th key={c.key} scope="col" className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey(r, i)} data-testid={`${testId}-row`} className={`border-t border-l-4 border-fieldiq-border ${rail ? rail(r) : 'border-l-transparent'}`}>
              {cols.map((c) => (
                <td key={c.key} className={`px-4 py-3 text-sm text-fieldiq-text ${c.align === 'right' ? 'text-right font-mono tabular-nums' : ''}`}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const hours = (n: number | null | undefined) => withUnit(n, 'h');
const days = (list: unknown) => {
  const d = arr<string>(list);
  return d.length ? d.map((x) => wtDay(x)).join(', ') : '—';
};

// ─── Shift summary ───────────────────────────────────────────────────────────

const figureCols = (): Col<WtShiftFigure>[] => [
  { key: 'label', header: 'Reading', render: (f) => <span className="font-semibold text-white">{text(f.label)}</span> },
  { key: 'first', header: 'First', align: 'right', render: (f) => withUnit(f.first, f.unit) },
  { key: 'last', header: 'Last', align: 'right', render: (f) => withUnit(f.last, f.unit) },
  { key: 'min', header: 'Low', align: 'right', render: (f) => withUnit(f.min, f.unit) },
  { key: 'max', header: 'High', align: 'right', render: (f) => withUnit(f.max, f.unit) },
];

export function ShiftSummaryFigures({ data }: { data: WtShiftSummaryData }) {
  const tz = useTenantTimeZone();
  const crew = arr<WtShiftSummaryData['crew'][number]>(data.crew);
  const units = arr<WtShiftSummaryData['units'][number]>(data.units);
  const pressures = arr<WtShiftFigure>(data.pressures);
  const rates = arr<WtShiftFigure>(data.rates);
  const cumulatives = arr<WtShiftCumulative>(data.cumulatives);
  const flags = data.flags ?? null;
  const open = arr<NonNullable<WtShiftSummaryData['flags']>['open'][number]>(flags?.open);
  const w = data.window ?? null;
  const shift = wtShiftLabel(data.shift);
  const title = [data.job?.jobNumber, wtDay(data.workDate), shift].filter((p) => p && p !== '—').join(' · ') || 'Shift summary';
  return (
    <div className="space-y-4" data-testid="wt-ai-shift-figures">
      <WtPanel eyebrow="Shift summary" title={title}>
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <WtSpec
            label="Job"
            value={data.job?.id ? <Link className="underline" to={`/well-testing/jobs/${data.job.id}`}>{text(data.job.jobNumber)}</Link> : '—'}
            mono
          />
          <WtSpec label="Customer" value={text(data.job?.customer)} />
          <WtSpec label="Well" value={text(data.job?.well)} />
          <WtSpec label="Shift" value={shift} />
          <WtSpec label="Window" value={w ? `${text(w.fromLocal)} → ${text(w.toLocal)}` : '—'} />
          <WtSpec label="Crew" value={displayValue(num(data.crewCount))} mono />
          <WtSpec label="Crew hours" value={hours(data.hours)} mono />
          <WtSpec label="Standby" value={hours(data.standbyHours)} mono />
          <WtSpec label="Unit hours" value={hours(data.unitHours)} mono />
          <WtSpec label="Readings" value={displayValue(num(data.readings?.count))} mono />
          <WtSpec label="First reading" value={formatInstant(data.readings?.firstAt ?? null, tz) ?? '—'} />
          <WtSpec label="Last reading" value={formatInstant(data.readings?.lastAt ?? null, tz) ?? '—'} />
          <WtSpec label="Load recovered" value={withUnit(data.loadRecoveredPct, '%')} mono />
          <WtSpec label="H2S high" value={withUnit(data.h2sMaxPpm, 'ppm')} mono />
          <div className="min-w-0" data-testid="wt-ai-shift-flags">
            <WtSpec label="Flags raised" value={displayValue(num(flags?.raised))} mono />
          </div>
          <WtSpec label="Unacknowledged" value={displayValue(num(flags?.unacknowledged))} mono />
          <WtSpec label="Missed intervals" value={displayValue(num(flags?.missedIntervals))} mono />
        </dl>
        {data.shiftLogId && (
          <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
            From the{' '}
            <Link className="font-bold text-fieldiq-text underline" to={`/well-testing/shift-logs/${data.shiftLogId}`} data-testid="wt-ai-shift-log-link">
              shift log
            </Link>{' '}
            and the readings in this window.
          </p>
        )}
      </WtPanel>
      <WtPanel eyebrow="Shift summary" title="Crew" testId="wt-ai-shift-crew">
        <AiTable
          rows={crew}
          testId="wt-ai-crew"
          rowKey={(c, i) => `${c.userId}:${i}`}
          empty="No crew was logged on this shift."
          minWidth="min-w-[420px]"
          cols={[
            { key: 'name', header: 'Name', render: (c) => <span className="font-semibold text-white">{text(c.name)}</span> },
            { key: 'role', header: 'Crew role', render: (c) => text(c.role) },
          ]}
        />
      </WtPanel>
      <WtPanel eyebrow="Shift summary" title="Units" testId="wt-ai-shift-units">
        <AiTable
          rows={units}
          testId="wt-ai-unit"
          rowKey={(u, i) => `${u.label}:${i}`}
          empty="No unit hours were logged on this shift."
          cols={[
            { key: 'unit', header: 'Unit', render: (u) => <span className="font-semibold text-white">{text(u.label)}</span> },
            { key: 'hours', header: 'Hours', align: 'right', render: (u) => hours(u.hours) },
            { key: 'standby', header: 'Standby', align: 'right', render: (u) => hours(u.standbyHours) },
          ]}
        />
      </WtPanel>
      <WtPanel eyebrow="Readings" title="Pressures" testId="wt-ai-pressures">
        <AiTable rows={pressures} testId="wt-ai-pressure" rowKey={(f) => f.key} empty="No pressure was read on this shift." cols={figureCols()} />
      </WtPanel>
      <WtPanel eyebrow="Readings" title="Rates" testId="wt-ai-rates">
        <AiTable rows={rates} testId="wt-ai-rate" rowKey={(f) => f.key} empty="No rate was read on this shift." cols={figureCols()} />
      </WtPanel>
      <WtPanel eyebrow="Readings" title="Cumulatives" testId="wt-ai-cumulatives">
        <AiTable
          rows={cumulatives}
          testId="wt-ai-cumulative"
          rowKey={(c) => c.key}
          empty="No cumulative volume was read on this shift."
          cols={[
            { key: 'label', header: 'Volume', render: (c) => <span className="font-semibold text-white">{text(c.label)}</span> },
            { key: 'start', header: 'Start', align: 'right', render: (c) => withUnit(c.start, c.unit) },
            { key: 'end', header: 'End', align: 'right', render: (c) => withUnit(c.end, c.unit) },
            { key: 'change', header: 'This shift', align: 'right', render: (c) => withUnit(c.change, c.unit) },
          ]}
        />
      </WtPanel>
      <WtPanel eyebrow="Readings" title="Open flags" testId="wt-ai-open-flags">
        <AiTable
          rows={open}
          testId="wt-ai-open-flag"
          rowKey={(f) => f.flagId}
          rail={() => 'border-l-fi-amber'}
          empty="No flag from this shift is waiting to be acknowledged."
          cols={[
            { key: 'label', header: 'Flag', render: (f) => <span className="font-semibold text-white">{text(f.label)}</span> },
            { key: 'field', header: 'Reading', render: (f) => text(f.field) },
            { key: 'at', header: 'At', render: (f) => formatInstant(f.at, tz) ?? '—' },
          ]}
        />
      </WtPanel>
      <WtPanel eyebrow="From the shift log" title="The crew's notes">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2">
          <div className="min-w-0">
            <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Handover</dt>
            <dd className="mt-1 whitespace-pre-wrap text-sm text-fieldiq-text">{text(data.handoverNote)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Safety notes</dt>
            <dd className="mt-1 whitespace-pre-wrap text-sm text-fieldiq-text">{text(data.safetyNotes)}</dd>
          </div>
        </dl>
      </WtPanel>
    </div>
  );
}

// ─── Preflight ───────────────────────────────────────────────────────────────

const READY_SKIN = { label: 'Ready', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' };
const NOT_READY_SKIN = { label: 'Not ready', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' };

const gapCols = (first: string): Col<WtPreflightRateGap>[] => [
  { key: 'label', header: first, render: (g) => <span className="font-semibold text-white">{text(g.label)}</span> },
  { key: 'days', header: 'Days', render: (g) => days(g.days) },
  { key: 'why', header: 'Why', render: (g) => text(g.reason) },
];

export function PreflightFigures({ data }: { data: WtPreflightData }) {
  const unpriced = arr<WtPreflightData['unpricedLines'][number]>(data.unpricedLines);
  const logs = arr<WtPreflightData['shiftLogsWithoutTicket'][number]>(data.shiftLogsWithoutTicket);
  const crew = arr<WtPreflightRateGap>(data.crewWithoutRate);
  const packages = arr<WtPreflightRateGap>(data.packagesWithoutRate);
  const units = arr<WtPreflightRateGap>(data.unitsWithoutRate);
  const rentIns = arr<WtPreflightRateGap>(data.rentInsWithoutRate);
  const title = data.ticket?.ticketNumber || (data.job?.jobNumber ? `${data.job.jobNumber} (whole job)` : 'Preflight');
  return (
    <div className="space-y-4" data-testid="wt-ai-preflight-figures">
      <WtPanel eyebrow="Preflight" title={title}>
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Ready to send</dt>
            <dd className="mt-1" data-testid="wt-ai-preflight-ready">
              {typeof data.ready === 'boolean' ? <WtBadge skin={data.ready ? READY_SKIN : NOT_READY_SKIN} value={String(data.ready)} /> : '—'}
            </dd>
          </div>
          <div className="min-w-0" data-testid="wt-ai-preflight-flags">
            <WtSpec label="Flags" value={displayValue(num(data.flagCount))} mono />
          </div>
          <WtSpec label="Tickets checked" value={displayValue(num(data.ticketsChecked))} mono />
          <WtSpec label="Currency" value={text(data.currency)} />
        </dl>
      </WtPanel>
      <WtPanel eyebrow="Preflight" title="Unpriced lines" testId="wt-ai-unpriced">
        <AiTable
          rows={unpriced}
          testId="wt-ai-unpriced-line"
          rowKey={(l) => l.lineId}
          rail={() => 'border-l-fi-amber'}
          empty="Every line has a price."
          cols={[
            { key: 'ticket', header: 'Ticket', render: (l) => (l.ticketId ? <Link className="font-mono underline" to={`/well-testing/tickets/${l.ticketId}`}>{text(l.ticketNumber)}</Link> : '—') },
            { key: 'line', header: 'Line', render: (l) => <span className="font-semibold text-white">{text(l.description)}</span> },
            { key: 'why', header: 'Why', render: (l) => text(l.reason) },
          ]}
        />
      </WtPanel>
      <WtPanel eyebrow="Preflight" title="Shift logs on no ticket" testId="wt-ai-unticketed">
        <AiTable
          rows={logs}
          testId="wt-ai-unticketed-log"
          rowKey={(l) => l.shiftLogId}
          rail={() => 'border-l-fi-amber'}
          empty="Every shift log on this job is on a ticket."
          minWidth="min-w-[360px]"
          cols={[
            { key: 'day', header: 'Work date', render: (l) => <Link className="underline" to={`/well-testing/shift-logs/${l.shiftLogId}`}>{wtDay(l.workDate)}</Link> },
            { key: 'shift', header: 'Shift', render: (l) => wtShiftLabel(l.shift) },
          ]}
        />
        {data.scope === 'ticket' && logs.length > 0 && (
          <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">These are the job's other shifts; they do not stop this ticket being sent.</p>
        )}
      </WtPanel>
      <WtPanel eyebrow="Preflight" title="Crew with no rate" testId="wt-ai-crew-gaps">
        <AiTable rows={crew} testId="wt-ai-crew-gap" rowKey={(g) => g.id} rail={() => 'border-l-fi-amber'} empty="Every crew member has a rate on the card." cols={gapCols('Crew member')} />
      </WtPanel>
      <WtPanel eyebrow="Preflight" title="Packages with no rate" testId="wt-ai-package-gaps">
        <AiTable rows={packages} testId="wt-ai-package-gap" rowKey={(g) => g.id} rail={() => 'border-l-fi-amber'} empty="Every package on the job has a rate on the card." cols={gapCols('Package')} />
      </WtPanel>
      <WtPanel eyebrow="Preflight" title="Units with no rate" testId="wt-ai-unit-gaps">
        <AiTable rows={units} testId="wt-ai-unit-gap" rowKey={(g) => g.id} rail={() => 'border-l-fi-amber'} empty="Every owned unit has a rate on the card." cols={gapCols('Unit')} />
      </WtPanel>
      <WtPanel eyebrow="Preflight" title="Rent-ins with no rate" testId="wt-ai-rentin-gaps">
        <AiTable rows={rentIns} testId="wt-ai-rentin-gap" rowKey={(g) => g.id} rail={() => 'border-l-fi-amber'} empty="Every billable rent-in has a billing rate." cols={gapCols('Rent-in')} />
      </WtPanel>
    </div>
  );
}

// ─── Test report draft ───────────────────────────────────────────────────────

/** The report the narrative is for. The report's own figures live on the report itself. */
export function ReportDraftFigures({ data }: { data: WtReportDraftData }) {
  return (
    <WtPanel eyebrow="Test report draft" title={text(data.report?.title) === '—' ? 'Test report' : text(data.report?.title)} testId="wt-ai-report-figures">
      <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3">
        <WtSpec
          label="Job"
          value={data.job?.id ? <Link className="underline" to={`/well-testing/jobs/${data.job.id}`}>{text(data.job.jobNumber)}</Link> : '—'}
          mono
        />
        <WtSpec label="Report status" value={text(data.report?.status)} />
      </dl>
      <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
        Approving writes this narrative into the report's summary, and only while the report is a draft.
      </p>
    </WtPanel>
  );
}

// ─── Compliance digest ───────────────────────────────────────────────────────

const stateCell = (s: string | null | undefined) => <WtBadge skin={wtAiStateSkin(s)} value={s ?? ''} />;
const jobsCell = (list: unknown) => {
  const j = arr<string>(list);
  return j.length ? j.join(', ') : '—';
};

export function ComplianceFigures({ data }: { data: WtComplianceDigestData }) {
  const crew = arr<WtComplianceDigestData['crewCerts'][number]>(data.crewCerts);
  const psv = arr<WtComplianceDigestData['psv'][number]>(data.psv);
  const calibration = arr<WtComplianceDigestData['calibration'][number]>(data.calibration);
  return (
    <div className="space-y-4" data-testid="wt-ai-compliance-figures">
      <WtPanel eyebrow="Compliance digest" title="Open well-test work">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-5">
          <WtSpec label="As of" value={wtDay(data.today)} />
          <WtSpec label="Items" value={displayValue(num(data.total))} mono />
          <WtSpec label="Crew certs" value={displayValue(crew.length)} mono />
          <WtSpec label="PSV recerts" value={displayValue(psv.length)} mono />
          <WtSpec label="Calibrations" value={displayValue(calibration.length)} mono />
        </dl>
      </WtPanel>
      <WtPanel eyebrow="Compliance" title="Crew certificates" testId="wt-ai-certs">
        <AiTable
          rows={crew}
          testId="wt-ai-cert-item"
          rowKey={(x, i) => `${x.userId}:${x.cert}:${i}`}
          rail={(x) => wtAiStateSkin(x.state).rail}
          empty="Everyone crewed on open work holds current certificates."
          cols={[
            { key: 'name', header: 'Crew member', render: (x) => <span className="font-semibold text-white">{text(x.name)}</span> },
            { key: 'cert', header: 'Certificate', render: (x) => text(x.cert) },
            { key: 'jobs', header: 'Jobs', render: (x) => jobsCell(x.jobNumbers) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Expires', render: (x) => wtDay(x.dueOn) },
          ]}
        />
      </WtPanel>
      <WtPanel eyebrow="Compliance" title="PSV recertifications" testId="wt-ai-psv">
        <AiTable
          rows={psv}
          testId="wt-ai-psv-item"
          rowKey={(x, i) => `${x.equipmentId}:${x.component ?? ''}:${i}`}
          rail={(x) => wtAiStateSkin(x.state).rail}
          empty="No pressure safety valve on an open job is expired, due or missing a recertification."
          cols={[
            { key: 'unit', header: 'Unit', render: (x) => <span className="font-semibold text-white">{text(x.label)}</span> },
            { key: 'component', header: 'Valve', render: (x) => text(x.component) },
            { key: 'package', header: 'Package', render: (x) => text(x.packageName) },
            { key: 'jobs', header: 'Jobs', render: (x) => jobsCell(x.jobNumbers) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Recert due', render: (x) => wtDay(x.dueOn) },
          ]}
        />
      </WtPanel>
      <WtPanel eyebrow="Compliance" title="Calibrations" testId="wt-ai-calibration">
        <AiTable
          rows={calibration}
          testId="wt-ai-calibration-item"
          rowKey={(x, i) => `${x.equipmentId}:${i}`}
          rail={(x) => wtAiStateSkin(x.state).rail}
          empty="No instrument on an open job is due a calibration."
          cols={[
            { key: 'unit', header: 'Unit', render: (x) => <span className="font-semibold text-white">{text(x.label)}</span> },
            { key: 'package', header: 'Package', render: (x) => text(x.packageName) },
            { key: 'jobs', header: 'Jobs', render: (x) => jobsCell(x.jobNumbers) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Cert expires', render: (x) => wtDay(x.dueOn) },
            {
              key: 'days',
              header: 'Working days',
              align: 'right',
              render: (x) => (x.daysUsed == null && x.dayLimit == null ? '—' : `${displayValue(num(x.daysUsed))} of ${displayValue(num(x.dayLimit))}`),
            },
          ]}
        />
      </WtPanel>
    </div>
  );
}

/** The figures for any run, by agent. Unknown agent or missing data says so rather than guessing. */
export function RunFigures({ agent, data }: { agent: string; data: unknown }) {
  if (!data || typeof data !== 'object') {
    return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">The figures for this run were not stored.</p>;
  }
  if (agent === 'wt_shift_summary') return <ShiftSummaryFigures data={data as WtShiftSummaryData} />;
  if (agent === 'wt_ticket_preflight') return <PreflightFigures data={data as WtPreflightData} />;
  if (agent === 'wt_test_report_draft') return <ReportDraftFigures data={data as WtReportDraftData} />;
  if (agent === 'wt_compliance_digest') return <ComplianceFigures data={data as WtComplianceDigestData} />;
  return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">This kind of draft is not shown here.</p>;
}

// ─── The AI draft and its review ─────────────────────────────────────────────

/** The heading says where the draft stands. A PENDING draft is waiting on a person and is not published. */
export const WT_DRAFT_HEADING: Record<string, string> = {
  pending: 'Draft — awaiting your approval',
  actioned: 'Approved',
  dismissed: 'Draft — dismissed',
  superseded: 'Draft — replaced by a newer run',
  no_action: 'Draft — no action',
};

function DraftText({ draft, testId }: { draft: WtAiDraft; testId: string }) {
  const obs = arr<string>(draft.observations);
  return (
    <div className="space-y-3" data-testid={testId}>
      <p className="whitespace-pre-wrap" data-testid={`${testId}-narrative`}>{text(draft.narrative)}</p>
      {obs.length > 0 && (
        <ul className="list-disc space-y-1 pl-5" data-testid={`${testId}-observations`}>
          {obs.map((o, i) => (
            <li key={i}>{o}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The AI's text. Approve / Edit / Dismiss only on a PENDING run and only for a
 * role that may review (the API refuses anyone else, and refuses a decided run
 * with 409 not_pending). A null draft is said plainly with the API's reason,
 * and a person may still write the narrative and approve that.
 */
export function WtAiDraftPanel({
  runId,
  outcome,
  draft,
  approved,
  approvedEdited,
  aiError,
  canReview,
}: {
  runId: string;
  outcome: string | null | undefined;
  draft: WtAiDraft | null | undefined;
  approved?: WtAiDraft | null;
  approvedEdited?: boolean | null;
  aiError: string | null | undefined;
  canReview: boolean;
}) {
  const review = useReviewWtAi();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [narrative, setNarrative] = useState('');
  const [observations, setObservations] = useState('');
  const pending = outcome === 'pending';
  const heading = (outcome && WT_DRAFT_HEADING[outcome]) || 'Draft';
  const busy = review.isPending;

  const startEdit = () => {
    setError(null);
    setNarrative(draft?.narrative ?? '');
    setObservations(arr<string>(draft?.observations).join('\n'));
    setEditing(true);
  };

  const approve = (edit?: { narrative: string; observations: string[] }) => {
    setError(null);
    review.mutate(
      { id: runId, action: 'approve', ...(edit ? { edit } : {}) },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Draft approved');
        },
        onError: (err) => setError(wtAiRefusal(err, 'The draft was not approved.')),
      },
    );
  };

  const dismiss = () => {
    setError(null);
    review.mutate(
      { id: runId, action: 'dismiss' },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Draft dismissed');
        },
        onError: (err) => setError(wtAiRefusal(err, 'The draft was not dismissed.')),
      },
    );
  };

  const submitEdit = () =>
    approve({
      narrative,
      observations: observations.split('\n').map((o) => o.trim()).filter(Boolean),
    });

  return (
    <section data-testid="wt-ai-draft" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">AI draft</p>
          <h2 className="mt-1 text-base font-extrabold tracking-tight text-white" data-testid="wt-ai-draft-heading">
            {heading}
          </h2>
        </div>
        {canReview && pending && !editing && (
          <div className="flex flex-wrap gap-2" data-testid="wt-ai-review-actions">
            <button type="button" className={WT_SECONDARY} onClick={dismiss} disabled={busy} data-testid="wt-ai-dismiss">
              <XCircle className="size-4" aria-hidden="true" />
              Dismiss
            </button>
            <button type="button" className={WT_SECONDARY} onClick={startEdit} disabled={busy} data-testid="wt-ai-edit">
              <Pencil className="size-4" aria-hidden="true" />
              {draft ? 'Edit, then approve' : 'Write and approve'}
            </button>
            {draft && (
              <button type="button" className={WT_PRIMARY} onClick={() => approve()} disabled={busy} data-testid="wt-ai-approve">
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
                Approve
              </button>
            )}
          </div>
        )}
      </header>
      {pending && (
        <p className="border-b border-fieldiq-border bg-fi-signal-amber/10 px-4 py-2 text-xs text-fieldiq-text" data-testid="wt-ai-not-published">
          Not approved yet. Nothing has been sent, filed or published — this draft waits for a person to approve or dismiss it.
        </p>
      )}
      {error && (
        <p role="alert" className="border-b border-fieldiq-border bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text" data-testid="wt-ai-review-error">
          {error}
        </p>
      )}
      <div className="space-y-3 px-4 py-4 text-sm text-fieldiq-text">
        {editing ? (
          <div className="space-y-3" data-testid="wt-ai-edit-form">
            <WtField label="Summary" hint={`Up to ${WT_AI_MAX_NARRATIVE} characters.`}>
              <textarea
                className={`${WT_INPUT} h-32 py-2`}
                aria-label="Summary"
                maxLength={WT_AI_MAX_NARRATIVE}
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                data-testid="wt-ai-edit-narrative"
              />
            </WtField>
            <WtField label="Observations" hint={`One per line; up to 8, each up to ${WT_AI_MAX_OBSERVATION} characters.`}>
              <textarea
                className={`${WT_INPUT} h-24 py-2`}
                aria-label="Observations"
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                data-testid="wt-ai-edit-observations"
              />
            </WtField>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={WT_SECONDARY} onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className={WT_PRIMARY} onClick={submitEdit} disabled={busy} data-testid="wt-ai-edit-approve">
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
                Approve this text
              </button>
            </div>
          </div>
        ) : outcome === 'actioned' && approved ? (
          <>
            <DraftText draft={approved} testId="wt-ai-approved" />
            <p className="text-xs text-fieldiq-muted">{approvedEdited ? 'Edited by the reviewer before approval.' : 'Approved as the AI drafted it.'}</p>
          </>
        ) : draft ? (
          <>
            <DraftText draft={draft} testId="wt-ai-text" />
            <p className="text-xs text-fieldiq-muted">Written by AI from the figures above, as an explanation only. It changes no figure.</p>
          </>
        ) : (
          <div className="space-y-1" data-testid="wt-ai-no-draft">
            <p className="font-bold">AI draft unavailable — the figures above are still correct.</p>
            {aiError && <p className="text-xs text-fieldiq-muted" data-testid="wt-ai-error">{aiError}</p>}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Launchers: the AI Drafts register and the job / ticket pages ────────────

/** The company's WT jobs as a select. The register filters by it and the register's shift summary picks from it. */
export function WtJobSelect({ value, onChange, label, testId, allLabel }: { value: string; onChange: (v: string) => void; label: string; testId: string; allLabel?: string }) {
  const jobs = useWtJobs();
  const list = jobs.data?.data ?? [];
  return (
    <select className={WT_INPUT} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="">{allLabel ?? 'Choose a job'}</option>
      {list.map((j) => (
        <option key={j.id} value={j.id}>
          {j.jobNumber}
          {j.customerName ? ` · ${j.customerName}` : ''}
        </option>
      ))}
      {value && !list.some((j) => j.id === value) && <option value={value}>This job</option>}
    </select>
  );
}

/**
 * Draft one job's shift summary (POST /well-testing/ai/shift-summary). The one
 * launcher, used twice: on the AI Drafts register it picks the job and opens
 * the new draft; on a job's page the job is given (`jobId`), the work date
 * starts at the tenant's today (`defaultWorkDate`) and the draft shows in
 * place. A blank work date is sent as nothing, and the API takes the tenant's
 * today. A refusal (409 nothing_to_summarise) reads in the API's words.
 */
export function WtShiftSummaryLauncher({ jobId: fixedJobId, defaultWorkDate, onRan }: {
  jobId?: string;
  defaultWorkDate?: string | null;
  onRan: (result: WtAiRunResult | null) => void;
}) {
  const run = useRunWtAi();
  const [pickedJobId, setPickedJobId] = useState('');
  const [shift, setShift] = useState<'day' | 'night'>('day');
  const [workDate, setWorkDate] = useState(defaultWorkDate ?? '');
  const [error, setError] = useState<string | null>(null);
  const jobId = fixedJobId ?? pickedJobId;
  const go = () => {
    setError(null);
    if (!jobId) {
      setError('Choose the job to summarise.');
      return;
    }
    run.mutate(
      { agent: 'wt_shift_summary', jobId, shift, ...(workDate ? { workDate } : {}) },
      {
        onSuccess: (r) => onRan(r),
        onError: (err) => setError(wtAiRefusal(err, 'The shift summary did not run.')),
      },
    );
  };
  return (
    <WtPanel eyebrow="AI draft" title="Shift summary" testId="wt-ai-shift-launcher">
      <div className="space-y-3 px-4 py-4">
        <WtFormError message={error} />
        <p className="text-xs text-fieldiq-muted">
          Totals one shift's log and readings exactly, then asks the AI to draft a summary. The draft waits for your approval; nothing is sent.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          {!fixedJobId && (
            <div className="w-full sm:w-72">
              <WtField label="Job">
                <WtJobSelect value={pickedJobId} onChange={setPickedJobId} label="Job to summarise" testId="wt-ai-shift-job" />
              </WtField>
            </div>
          )}
          <div className="w-full sm:w-40">
            <WtField label="Shift">
              <select className={WT_INPUT} aria-label="Shift" value={shift} onChange={(e) => setShift(e.target.value === 'night' ? 'night' : 'day')} data-testid="wt-ai-shift-shift">
                <option value="day">Day</option>
                <option value="night">Night</option>
              </select>
            </WtField>
          </div>
          <div className="w-full sm:w-48">
            <WtField label="Work date" hint="Blank means today.">
              <input type="date" className={WT_DATE_INPUT} aria-label="Work date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} data-testid="wt-ai-shift-date" />
            </WtField>
          </div>
          <button type="button" className={WT_PRIMARY} onClick={go} disabled={run.isPending} data-testid="wt-ai-run-shift">
            {run.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Sparkles className="size-4" aria-hidden="true" />}
            {run.isPending ? 'Drafting…' : 'Draft shift summary'}
          </button>
        </div>
      </div>
    </WtPanel>
  );
}

/**
 * A run just made, shown in place (job page, ticket page). The POST answers
 * without an outcome, so the stored run is read for it (and re-read after a
 * review); until that loads, a fresh run is pending by definition. PF's
 * PfAiRunInPlace.
 */
export function WtAiRunInPlace({ result, canReview }: { result: WtAiRunResult; canReview: boolean }) {
  const stored = useWtAiDigest(result.agentRunId);
  const s = stored.data;
  return (
    <div className="space-y-4" data-testid="wt-ai-in-place">
      <RunFigures agent={String(result.agent)} data={result.data} />
      <WtAiDraftPanel
        runId={result.agentRunId}
        outcome={s?.outcome ?? 'pending'}
        draft={s ? s.draft : result.draft}
        approved={s?.approved ?? null}
        approvedEdited={s?.approvedEdited ?? null}
        aiError={s ? s.aiError : result.aiError}
        canReview={canReview}
      />
      <p className="text-xs text-fieldiq-muted">
        Kept with the{' '}
        <Link to={`/well-testing/ai/${result.agentRunId}`} className="font-bold text-fieldiq-text underline" data-testid="wt-ai-run-link">
          AI drafts
        </Link>
        .
      </p>
    </div>
  );
}

export function WtPreflightButton({ busy, onClick, className, testId }: { busy: boolean; onClick: () => void; className: string; testId: string }) {
  return (
    <button type="button" className={className} onClick={onClick} disabled={busy} data-testid={testId}>
      {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ListChecks className="size-4" aria-hidden="true" />}
      {busy ? 'Checking…' : 'Preflight check'}
    </button>
  );
}

/** Shift logs tab: draft one shift's summary of THIS job and show it in place. */
export function WtJobShiftSummary({ jobId, today, canReview }: { jobId: string; today: string | null; canReview: boolean }) {
  const [result, setResult] = useState<WtAiRunResult | null>(null);
  return (
    <div className="space-y-4" data-testid="wt-ai-job-shift-summary">
      <WtShiftSummaryLauncher jobId={jobId} defaultWorkDate={today} onRan={setResult} />
      {result && <WtAiRunInPlace result={result} canReview={canReview} />}
    </div>
  );
}

/** Tickets tab: preflight the whole job (its tickets neither customer-approved nor void, and its shift logs). */
export function WtJobPreflight({ jobId, canReview }: { jobId: string; canReview: boolean }) {
  const run = useRunWtAi();
  const [result, setResult] = useState<WtAiRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const go = () => {
    setError(null);
    run.mutate(
      { agent: 'wt_ticket_preflight', jobId },
      {
        onSuccess: (r) => setResult(r ?? null),
        onError: (err) => setError(wtAiRefusal(err, 'The preflight did not run.')),
      },
    );
  };
  return (
    <div className="space-y-4" data-testid="wt-ai-job-preflight">
      <WtPanel
        eyebrow="Before sending"
        title="Job preflight"
        actions={<WtPreflightButton busy={run.isPending} onClick={go} className={WT_SECONDARY} testId="wt-ai-run-job-preflight" />}
      >
        <div className="space-y-2 px-4 py-4">
          <WtFormError message={error} />
          <p className="text-xs text-fieldiq-muted">
            Checks this job's open tickets and shift logs for unpriced lines, shifts on no ticket, and crew, packages, units or rent-ins with no rate. The AI's text explains the flags; it changes nothing.
          </p>
        </div>
      </WtPanel>
      {result && <WtAiRunInPlace result={result} canReview={canReview} />}
    </div>
  );
}
